import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useCallback } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Tooltip,
    Snackbar,
    Alert,
    Switch,
    FormControlLabel,
    CircularProgress,
} from '@mui/material';
import { popupAPI } from '../../services/apiService';
import './LandingPopup.css';

function LandingPopup() {
    const { t, i18n } = useTranslation();
    const token = localStorage.getItem('token');
    const group = localStorage.getItem('userGroup');
    const [popupForm, setPopupForm] = useState({
        status: false,
        title: '',
        content: '',
    });
    const [originalPopup, setOriginalPopup] = useState({
        status: false,
        title: '',
        content: '',
    });
    const [popupLoading, setPopupLoading] = useState(true);
    const [popupSaving, setPopupSaving] = useState(false);
    const [error, setError] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    useEffect(() => {
        const fetchPopup = async () => {
            try {
                setPopupLoading(true);

                const response = await popupAPI.getPopup();
                const data = response?.data ?? response;

                const normalizedPopup = {
                    status: Boolean(data?.status),
                    title: data?.title || '',
                    content: data?.content || '',
                };

                setPopupForm(normalizedPopup);
                setOriginalPopup(normalizedPopup);
            } catch (err) {
                console.error(err);
                setError(err.message);

                setSnackbar({
                    open: true,
                    message: err.message || t('generic_error'),
                    severity: 'error',
                });
            } finally {
                setPopupLoading(false);
            }
        };

        fetchPopup();
    }, [t]);
    const isPopupChanged =
        popupForm.status !== originalPopup.status ||
        popupForm.content !== originalPopup.content ||
        popupForm.title !== originalPopup.title;

    const handlePopupSubmit = async () => {
        if (!isPopupChanged || popupSaving) return;

        try {
            setPopupSaving(true);

            const payload = {
                status: popupForm.status,
                title: popupForm.title,
                content: popupForm.content,
            };

            const response = await popupAPI.updatePopup(payload);
            const data = response?.data ?? response;

            const updatedPopup = {
                status: Boolean(data?.status ?? payload.status),
                content: data?.content ?? payload.content,
            };

            setPopupForm(updatedPopup);
            setOriginalPopup(updatedPopup);

            setSnackbar({
                open: true,
                message: t('changes_saved_successfully') || 'Changes saved successfully',
                severity: 'success',
            });
        } catch (err) {
            console.error(err);

            setSnackbar({
                open: true,
                message: err.message || t('generic_error'),
                severity: 'error',
            });
        } finally {
            setPopupSaving(false);
        }
    };
    return (
        <div className="popup-settings-box">
            <p className="popup-settings-title">{t('popup_settings')}</p>

            {popupLoading ? (
                <div className="popup-loading">
                    <CircularProgress size={28} />
                </div>
            ) : (
                <>
                    <FormControlLabel
                        className="popup-switch-row"
                        control={
                            <Switch
                                checked={popupForm.status}
                                onChange={(e) =>
                                    setPopupForm((prev) => ({
                                        ...prev,
                                        status: e.target.checked,
                                    }))
                                }
                                color="success"
                            />
                        }
                        label={popupForm.status ? t('active') : t('inactive')}
                    />
                    <div className="popupInfoSection">
                        <p>{t('notification_title_lable')}</p>
                        <TextField
                            className="popup-content-input"
                            fullWidth
                            multiline
                            maxRows={3}
                            value={popupForm.title}
                            onChange={(e) =>
                                setPopupForm((prev) => ({
                                    ...prev,
                                    title: e.target.value,
                                }))
                            }
                            placeholder={t('popup_title_placeholder')}
                            variant="outlined"
                            sx={{
                                direction: 'rtl',
                                marginTop: '5px',
                            }}
                        />
                        <p>{t('notification_content_lable')}</p>
                        <TextField
                            className="popup-content-input"
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={8}
                            value={popupForm.content}
                            onChange={(e) =>
                                setPopupForm((prev) => ({
                                    ...prev,
                                    content: e.target.value,
                                }))
                            }
                            placeholder={t('popup_placeholder')}
                            variant="outlined"
                            sx={{
                                direction: 'rtl',
                                marginTop: '5px',
                            }}
                        />
                    </div>
                    <Button
                        fullWidth
                        variant={isPopupChanged ? 'contained' : 'outlined'}
                        color={isPopupChanged ? 'success' : 'inherit'}
                        disabled={!isPopupChanged || popupSaving}
                        onClick={handlePopupSubmit}
                        className={`popup-submit ${isPopupChanged ? 'active' : ''}`}
                        sx={{
                            marginTop: '14px',
                            fontWeight: 'bold',
                            borderRadius: '8px',
                        }}
                    >
                        {popupSaving ? t('saving') : t('submit_changes')}
                    </Button>
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={3000}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </>
            )}
        </div>
    );
}

export default LandingPopup;
